/**
 * Recent Sites Utility
 *
 * Manages persistence of recently visited sites in localStorage.
 * Stores up to 5 most recent sites with their ID and name.
 */

const STORAGE_KEY = 'recentSites';
const MAX_RECENT_SITES = 5;

export interface RecentSite {
  readonly siteId: string;
  readonly name: string;
  readonly visitedAt: number;
}

/**
 * Retrieves the list of recent sites from localStorage.
 * Returns an empty array if no sites are stored or if parsing fails.
 */
export function getRecentSites(): readonly RecentSite[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored) as RecentSite[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Adds a site to the recent sites list.
 * If the site already exists, it moves to the top.
 * Maintains a maximum of 5 recent sites.
 */
export function addRecentSite(siteId: string, name: string): void {
  const existing = getRecentSites();
  
  // Remove existing entry for this site if present
  const filtered = existing.filter(site => site.siteId !== siteId);
  
  // Add new entry at the beginning
  const newEntry: RecentSite = {
    siteId,
    name,
    visitedAt: Date.now(),
  };
  
  const updated = [newEntry, ...filtered].slice(0, MAX_RECENT_SITES);
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Clears all recent sites from localStorage.
 */
export function clearRecentSites(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}
